"use client"

import { AnimatePresence, motion, useReducedMotion } from "framer-motion"
import { type ReactNode, useEffect, useRef } from "react"
import useMeasure from "react-use-measure"
import {
  COLUMN_DURATION,
  CROSSFADE_DURATION,
  ENTER_DURATION,
  ENTER_EASE,
  ITEMS_DELAY,
} from "./landingMotion"

export function LandingListPage({
  href,
  header,
  maxWidthClassName,
  fadeContent = false,
  children,
}: {
  href: string
  header: ReactNode
  maxWidthClassName: string
  fadeContent?: boolean
  children: ReactNode
}) {
  const reduceMotion = useReducedMotion()
  const hasMounted = useRef(false)

  useEffect(() => {
    hasMounted.current = true
  }, [])

  return (
    <section className="h-full select-none">
      <MeasuredHeight enabled={!reduceMotion}>
        <AnimatePresence initial={false} mode="popLayout">
          <motion.div
            key={href}
            className={`mx-auto w-full ${maxWidthClassName}`}
            initial={false}
            animate={{ opacity: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0 }}
            transition={{
              duration: CROSSFADE_DURATION,
              ease: ENTER_EASE,
            }}
          >
            <motion.div
              initial={
                reduceMotion || !hasMounted.current ? false : { opacity: 0 }
              }
              animate={{ opacity: 1 }}
              transition={{
                duration: CROSSFADE_DURATION,
                ease: ENTER_EASE,
              }}
            >
              {header}
            </motion.div>

            <motion.div
              initial={
                reduceMotion || !fadeContent || !hasMounted.current
                  ? false
                  : { opacity: 0, y: 8 }
              }
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: ENTER_DURATION,
                ease: ENTER_EASE,
                delay: reduceMotion || !fadeContent ? 0 : ITEMS_DELAY,
              }}
            >
              {children}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </MeasuredHeight>
    </section>
  )
}

function MeasuredHeight({
  enabled,
  children,
}: {
  enabled: boolean
  children: ReactNode
}) {
  const [ref, bounds] = useMeasure({ offsetSize: true })
  const skipAnimation = useRef(true)

  useEffect(() => {
    if (bounds.height > 0) {
      skipAnimation.current = false
    }
  }, [bounds.height])

  return (
    <motion.div
      className="relative w-full"
      initial={false}
      animate={{ height: bounds.height > 0 ? bounds.height : "auto" }}
      transition={
        !enabled || skipAnimation.current
          ? { duration: 0 }
          : { duration: COLUMN_DURATION, ease: ENTER_EASE }
      }
    >
      <div ref={ref} className="w-full">
        {children}
      </div>
    </motion.div>
  )
}
